import * as Sentry from '@sentry/react';
import { get } from 'lodash';

type ErrorRecord = {
  lastReported: number;
  totalSends: number;
};

type EventRecords = {
  [key: string]: ErrorRecord;
};

const ONE_SECOND = 1000;
const MAX_ERROR_SENDS = 40;
const TEN_MINUTES = 600000;

const initSentryEventRecordsManager = () => {
  let eventRecords: EventRecords = {};

  // Reset every 10 min for long sessions
  setInterval(() => {
    eventRecords = {};
  }, TEN_MINUTES);

  const getErrorRecord = (errorName: string) => {
    const errorRecord = eventRecords[errorName];

    if (errorRecord) {
      return { ...errorRecord };
    }
  };

  const setErrorRecord = (errorName: string, errorRecord: ErrorRecord) => {
    eventRecords[errorName] = { ...errorRecord };
  };

  return { getErrorRecord, setErrorRecord };
};

const EventRecords = initSentryEventRecordsManager();

const shouldDropEvent = (errorName: string): boolean => {
  const errorRecord = EventRecords.getErrorRecord(errorName);
  let shouldDrop = false;

  if (errorRecord) {
    const recentlyReported = Date.now() - errorRecord.lastReported < ONE_SECOND;
    const maxSendsMet = errorRecord.totalSends > MAX_ERROR_SENDS;

    shouldDrop = recentlyReported || maxSendsMet;

    const totalSends = shouldDrop
      ? errorRecord.totalSends
      : errorRecord.totalSends + 1;
    EventRecords.setErrorRecord(errorName, {
      lastReported: Date.now(),
      totalSends,
    });
  } else {
    EventRecords.setErrorRecord(errorName, {
      lastReported: Date.now(),
      totalSends: 1,
    });
  }

  return shouldDrop;
};

const getUniqueNameFromStacktrace = (stacktrace: Sentry.Stacktrace) => {
  if (
    stacktrace.frames &&
    stacktrace.frames.length &&
    stacktrace.frames[stacktrace.frames.length - 1]
  ) {
    const lastFrame = stacktrace.frames[stacktrace.frames.length - 1];

    if (
      lastFrame &&
      lastFrame.filename &&
      lastFrame.colno &&
      lastFrame.lineno
    ) {
      return `${lastFrame.filename} ${lastFrame.colno} ${lastFrame.lineno}`;
    }
  }
};

const getExceptionErrorName = (event: Sentry.Event): string | undefined => {
  const exception: Sentry.Exception | undefined = get(
    event,
    'exception.values[0]',
  );

  if (exception) {
    const { type, value, stacktrace } = exception;

    // Promise rejections do not have stacktrace to create unique name
    if (type === 'UnhandledRejection' && value) {
      return value;
    }

    if (stacktrace) {
      return getUniqueNameFromStacktrace(stacktrace);
    }
  }
};

export const shouldRateLimitEvent = (event: Sentry.Event): boolean => {
  // If not an exception should have a unique name
  const errorName = event.exception
    ? getExceptionErrorName(event)
    : event.message;

  if (errorName) {
    return shouldDropEvent(errorName);
  }

  // Not sure if this will ever be met but if so send
  return false;
};
